import type CreativeSDK from '@cesdk/cesdk-js';
import {
	boundingBoxesOverlap,
	getBackground,
	getBackgroundId,
	getBoundingBox,
	getOverlappingBoundingBox,
	getPage,
	getPageId,
	getPageSide,
	getSelectionBoundingBox,
	getUserAddedBlocks,
	getUserAddedBlocksOnPage,
	isBackgroundBlock,
	isBlockWithFill,
	isFillBlock,
	isGraphicBlock,
	isGraphicOrTextBlock,
	isImageBlock,
	isPageBlock,
	isTextBlock,
	isUserAddedBlock,
} from '@theprintbar/shared';

// The pure engine helpers now live in @theprintbar/shared/commerce/cesdk so the
// Node worker (@cesdk/node) can reuse them for server-side artwork re-renders.
// Re-exported here so the editor's existing import paths keep working.
export {
	boundingBoxesOverlap,
	getBackground,
	getBackgroundId,
	getBlockMimeType,
	getBoundingBox,
	getOverlappingBoundingBox,
	getPage,
	getPageId,
	getPageSide,
	getSelectionBoundingBox,
	getUserAddedBlocks,
	getUserAddedBlocksOnPage,
	isBackgroundBlock,
	isBlockWithFill,
	isExclusionBlock,
	isFillBlock,
	isGraphicBlock,
	isGraphicOrTextBlock,
	isImageBlock,
	isNeckLabelBlock,
	isPageBlock,
	isTextBlock,
	isUserAddedBlock,
} from '@theprintbar/shared';
export type { BoundingBox } from '@theprintbar/shared';

// Stays editor-side: mutates blocks and pushes an undo step, which only makes
// sense in an interactive session.
export function resizeGroup(editor: CreativeSDK, blocks: number[], targetWidth: number, targetHeight: number) {
	const selectionBBox = getSelectionBoundingBox(editor.engine, blocks);

	const scaleX = targetWidth / selectionBBox.width;
	const scaleY = targetHeight / selectionBBox.height;

	const selectionCenterX = selectionBBox.left + selectionBBox.width / 2;
	const selectionCenterY = selectionBBox.top + selectionBBox.height / 2;

	blocks.forEach(block => {
		const blockType = editor.engine.block.getType(block);
		const bbox = getBoundingBox(editor.engine, block);
		const blockCenterX = bbox.left + bbox.width / 2;
		const blockCenterY = bbox.top + bbox.height / 2;
		const offsetX = blockCenterX - selectionCenterX;
		const offsetY = blockCenterY - selectionCenterY;
		const newOffsetX = offsetX * scaleX;
		const newOffsetY = offsetY * scaleY;
		const newBlockCenterX = selectionCenterX + newOffsetX;
		const newBlockCenterY = selectionCenterY + newOffsetY;
		const newWidth = bbox.width * scaleX;
		const newHeight = bbox.height * scaleY;

		if (isTextBlock(blockType)) {
			const scaleToUse = Math.max(scaleX, scaleY);
			const sizes = editor.engine.block.getTextFontSizes(block);
			editor.engine.block.setTextFontSize(block, Math.floor(sizes[0] * scaleToUse));
		} else {
			editor.engine.block.setWidth(block, newWidth);
			editor.engine.block.setHeight(block, newHeight);
		}

		editor.engine.block.setPositionX(block, newBlockCenterX - newWidth / 2);
		editor.engine.block.setPositionY(block, newBlockCenterY - newHeight / 2);
	});

	editor.engine.editor.addUndoStep();
}

export default {
	getPageSide,
	getPageId,
	getPage,
	getBackgroundId,
	getBackground,
	getBoundingBox,
	boundingBoxesOverlap,
	getOverlappingBoundingBox,
	getUserAddedBlocks,
	getUserAddedBlocksOnPage,
	isPageBlock,
	isBackgroundBlock,
	isTextBlock,
	isGraphicBlock,
	isFillBlock,
	isGraphicOrTextBlock,
	isBlockWithFill,
	isImageBlock,
	isUserAddedBlock,
};
